import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  makeStyles, tokens,
  Field, Input, Textarea, RadioGroup, Radio, Checkbox, Button, Spinner,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
} from '@fluentui/react-components';
import payseraImage from '../assets/img/paysera.svg';
import LegalTextDialog from '../components/LegalTextDialog';
import { useAuth } from '../context/AuthContext';
import { API_BASE, IS_DEV, PAYSERA_URL } from '../config.js';
import { apiGet } from '../services/api';
import { Breadcrumb } from '../theme/primitives';
import { iconMap } from '../theme/icons';
import { stonePalette } from '../theme/brand';
import { formatPrice } from '../utils/format';

const PersonIcon = iconMap.person;
const ShippingIcon = iconMap.local_shipping;
const BagIcon = iconMap.shopping_bag;
const CloseIcon = iconMap.close;
const ErrorIcon = iconMap.error;
const OfferIcon = iconMap.local_offer;

const getFurnitureLabel = (item, t) => {
  const type = item.settings?.type ?? item.type;
  return (type >= 0 && type <= 3) ? t(`furniture_types.type_${type}`) : t('checkout_page.furniture_fallback');
};

const HANDLE_KEYS = {
  0: 'handle_0', 1: 'handle_1', 2: 'handle_2',
  3: 'handle_3', 4: 'handle_4', 5: 'handle_5', 6: 'handle_6',
};

function getHandleLabel(doorHandles, t) {
  if (!doorHandles || typeof doorHandles !== 'object') return null;
  const values = Object.values(doorHandles);
  if (!values.length) return null;
  const freq = {};
  values.forEach(v => { freq[v] = (freq[v] || 0) + 1; });
  const dominant = Object.entries(freq).sort((a, b) => b[1] - a[1])[0]?.[0];
  return dominant != null ? (HANDLE_KEYS[dominant] ? t(`checkout_page.${HANDLE_KEYS[dominant]}`) : null) : null;
}

function getPlateLabel(plate) {
  if (!plate) return null;
  if (typeof plate === 'string') return plate;
  return plate.code || plate.name || null;
}

const API = `${API_BASE}`;

// Griffel priima keyframe apibrėžimą kaip paprastą objektą tiesiai `animationName`
// reikšmėje (be atskiro `keyframes()` helperio — @fluentui/react-components jo
// neeksportuoja, bet @griffel/core resolveStyleRules specialiai apdoroja šį atvejį).
const slideDownKeyframes = {
  from: { opacity: 0, transform: 'translateY(-6px)' },
  to: { opacity: 1, transform: 'translateY(0)' },
};

// Plonas apvalkalas ties realiais Fluent `Field`+`Input`/`Textarea` komponentais —
// išlaiko senojo lokalaus `Field` helperio kvietimo signatūrą (label/as='textarea'/...),
// bet viduje naudoja TIKRUS Fluent primityvus vietoj rankomis rašyto <label>+<input>.
// `validationState`/`validationMessage` keliauja į `Field` (žr. 40 punktų
// sąrašo site #4) — klaida rodoma PRIE SAVO lauko, ne bendroje klaidos juostoje.
function FormField({ label, as, required, validationState, validationMessage, ...props }) {
  return (
    <Field label={label} required={required} validationState={validationState} validationMessage={validationMessage}>
      {as === 'textarea'
        ? <Textarea resize="none" rows={3} {...props} />
        : <Input {...props} />}
    </Field>
  );
}

const useStyles = makeStyles({
  root: {
    minHeight: '100vh',
    backgroundColor: stonePalette[50],
  },
  // Standartinis breadcrumb+title antraštės blokas — tas pats paternas, kaip
  // /baldai/:categoryid (žr. #11), pakeičia buvusį AppHeader+CheckoutSteps.
  headerBar: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  headerInner: {
    maxWidth: '80rem', marginLeft: 'auto', marginRight: 'auto',
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    paddingTop: tokens.spacingVerticalXL, paddingBottom: tokens.spacingVerticalXL,
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  pageTitle: {
    fontSize: tokens.fontSizeBase600, lineHeight: tokens.lineHeightBase600,
    fontWeight: tokens.fontWeightBold, color: tokens.colorNeutralForeground1,
    letterSpacing: '-0.01em', margin: 0,
    '@media (min-width: 1024px)': { fontSize: tokens.fontSizeHero700, lineHeight: tokens.lineHeightHero700 },
  },

  // Prekės detalių dialogas — standartinis Fluent Dialog. Nuotrauka kairėje,
  // turinys (su antrašte viršuje) dešinėje — laikomasi to paties standarto,
  // kaip ir kituose prekės peržiūros modaluose (žr. /admin/bugs "Paveiksliukas
  // turi būti kairėje, o turinys dešinėje, title turi būti viršuje").
  modalSurface: { maxWidth: '34rem', width: '100%', overflow: 'hidden' },
  modalLayout: { display: 'flex', flexDirection: 'row', alignItems: 'stretch' },
  modalImage: { width: '11rem', flexShrink: 0, objectFit: 'cover', display: 'block' },
  // Be maxHeight/overflowY — anksčiau nereikalingai apkarpydavo turinį ir
  // rodydavo scrollbar'ą net kai viskas telpa (žr. /admin/bugs "Neturi buti
  // scrollbar"). Papildomas kairysis paddingas garantuoja tarpą nuo
  // nuotraukos, nepriklausomai nuo Fluent DialogBody numatytojo paddingo.
  modalBodyCol: { flex: 1, minWidth: 0, paddingLeft: tokens.spacingHorizontalM },
  modalCode: {
    fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3,
    fontFamily: tokens.fontFamilyMonospace, marginBottom: tokens.spacingVerticalM, marginTop: 0,
  },
  modalRows: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalSNudge,
    fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2,
  },
  modalRow: { display: 'flex', justifyContent: 'space-between', gap: tokens.spacingHorizontalM },
  modalRowValue: { fontWeight: tokens.fontWeightMedium, color: tokens.colorNeutralForeground1 },
  modalTotalRow: {
    display: 'flex', justifyContent: 'space-between',
    paddingTop: tokens.spacingVerticalS, marginTop: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  modalTotalLabel: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  modalTotalValue: { fontWeight: tokens.fontWeightBold, color: tokens.colorNeutralForeground1 },

  // Privatumo politikos/Sąlygų ir taisyklių nuorodos + varnelė prieš mokėjimą
  // (žr. /admin/bugs "Pakeisti teksta, checkbox vaildacija ir dialog langas") —
  // turinys parodomas dialog lange (fetch'inamas per /api/textpages/:slug, tas
  // pats šaltinis kaip /taisykles ir /privatumo-politika puslapiai, žr. Text.jsx),
  // ne naviguojant į atskirą puslapį, kad pirkėjas neprarastų užpildytos formos.
  // fontSize:'inherit' PAPILDOMAI prie font:'inherit' (žr. /admin/bugs "zalios
  // nuorodos teksto dydis turi buti toks pat standartinis kaip ir teksto") —
  // globalus index.css "button { font-family: inherit }" reset'as palikdavo
  // šio <button> font-size ties naršyklės numatytuoju dydžiu, ne aplinkinio
  // teksto dydžiu.
  legalLink: {
    color: tokens.colorBrandForegroundLink, textDecorationLine: 'underline', textUnderlineOffset: '2px',
    cursor: 'pointer', border: 'none', background: 'none', padding: 0, font: 'inherit', fontSize: 'inherit',
    ':hover': { color: tokens.colorBrandForegroundLinkHover, textDecorationLine: 'none' },
  },
  // fontSizeBase300 (ne mažesnis 200) — tas pats standartinis dydis, kaip ir
  // checkboxLabel žemiau, kad abi teisinės nuorodos (Privatumo politika ir
  // Sąlygomis ir taisyklėmis) atrodytų vienodo, standartinio dydžio.
  privacyNotice: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground3, marginTop: 0, marginBottom: tokens.spacingVerticalM },
  termsCheckboxRow: { marginBottom: tokens.spacingVerticalM },

  // Išdėstymas
  container: {
    maxWidth: '80rem', marginLeft: 'auto', marginRight: 'auto',
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    paddingTop: tokens.spacingVerticalXXXL, paddingBottom: tokens.spacingVerticalXXXL,
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  mainGrid: {
    display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalXXL,
    '@media (min-width: 1024px)': { gridTemplateColumns: '2fr 1fr' },
  },
  // DOM tvarka lieka [duomenys, santrauka] (klaviatūra/ekrano skaitytuvui logiška —
  // pirmiau pildai formą), bet MOBILIAME (viena kolona) santrauka vizualiai keliama
  // PIRMIAU per `order` — patvirtinta UX praktika: pirkėjas mobiliame turi iškart
  // matyti KĄ perka/kiek moka, prieš pradėdamas pildyti ilgą formą.
  leftCol: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXL, order: 2, '@media (min-width: 1024px)': { order: 0 } },
  rightCol: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, order: 1, '@media (min-width: 1024px)': { order: 0 } },

  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusXLarge,
    padding: tokens.spacingHorizontalXXL,
    transitionProperty: 'box-shadow, border-color',
    transitionDuration: tokens.durationNormal,
  },
  cardHeader: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, marginBottom: tokens.spacingVerticalXL },
  iconChip: {
    width: '32px', height: '32px', borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground2,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  chipIcon: { fontSize: '18px' },
  cardTitle: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, margin: 0, fontSize: tokens.fontSizeBase400 },

  fieldsGrid: {
    display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalL,
    '@media (min-width: 640px)': { gridTemplateColumns: '1fr 1fr' },
  },
  spanTwo: { '@media (min-width: 640px)': { gridColumnStart: 'span 2' } },

  checkboxRow: { marginTop: tokens.spacingVerticalL },
  checkboxLabel: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2, fontWeight: tokens.fontWeightRegular, cursor: 'pointer' },

  companyFields: {
    display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalL,
    marginTop: tokens.spacingVerticalL, paddingTop: tokens.spacingVerticalL,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    animationName: slideDownKeyframes, animationDuration: tokens.durationFast, animationTimingFunction: 'ease-out',
    '@media (min-width: 640px)': { gridTemplateColumns: '1fr 1fr' },
  },

  deliveryOptions: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalXL,
    '@media (min-width: 640px)': { flexDirection: 'row', gap: tokens.spacingHorizontalL },
  },
  deliveryAddress: {
    display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalL,
    animationName: slideDownKeyframes, animationDuration: tokens.durationFast, animationTimingFunction: 'ease-out',
    '@media (min-width: 640px)': { gridTemplateColumns: '1fr 1fr' },
  },
  commentsWrap: { marginTop: tokens.spacingVerticalL },

  emptyCartText: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground3 },
  cartList: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalSNudge, marginBottom: tokens.spacingVerticalL },
  cartRow: {
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    fontSize: tokens.fontSizeBase300, padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium, textAlign: 'left', gap: tokens.spacingHorizontalS,
    backgroundColor: 'transparent', border: 'none', cursor: 'pointer', font: 'inherit',
    transitionProperty: 'background-color', transitionDuration: tokens.durationNormal,
    ':hover': { backgroundColor: tokens.colorNeutralBackground2 },
  },
  cartRowLeft: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, minWidth: 0 },
  cartRowImg: { width: '32px', height: '32px', borderRadius: tokens.borderRadiusMedium, objectFit: 'cover', flexShrink: 0 },
  cartRowLabel: { color: tokens.colorNeutralForeground2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cartRowPrice: { fontWeight: tokens.fontWeightMedium, flexShrink: 0, color: tokens.colorNeutralForeground1 },

  divider: { height: '1px', backgroundColor: tokens.colorNeutralStroke2, marginTop: tokens.spacingVerticalM, marginBottom: tokens.spacingVerticalM },
  dividerMb: { height: '1px', backgroundColor: tokens.colorNeutralStroke2, marginBottom: tokens.spacingVerticalM },

  // Anksčiau čia buvo marginBottom:0, o giftBlock (naudojamas KITIEMS dviem tos
  // pačios checkbox eilučių sąrašo elementams) turėjo spacingVerticalM — todėl
  // pirmas tarpas (nuolaidos kodas → dovanų čekis) atrodė "suspaustas", o
  // paskutinis (dovanų čekis → avansas) — pastebimai didesnis (žr. /admin/bugs
  // "Atstumai tarp checkbox list item turi buti vienodi").
  discountBlock: { marginBottom: tokens.spacingVerticalM },
  discountHint: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase200, color: tokens.colorBrandForeground2,
    marginBottom: tokens.spacingVerticalS,
  },
  giftBlock: { marginBottom: tokens.spacingVerticalM },
  discountCheckboxRow: { marginBottom: 0 },
  discountReveal: { animationName: slideDownKeyframes, animationDuration: tokens.durationFast, animationTimingFunction: 'ease-out' },
  discountInputRow: { display: 'flex', gap: tokens.spacingHorizontalS },
  discountInput: { flexGrow: 1 },
  dangerOutlineBtn: {
    borderColor: tokens.colorStatusDangerBorder1, color: tokens.colorStatusDangerForeground1,
    ':hover': { backgroundColor: tokens.colorStatusDangerBackground1, borderColor: tokens.colorStatusDangerBorder1, color: tokens.colorStatusDangerForeground1 },
  },
  whitespaceNoWrap: { whiteSpace: 'nowrap' },
  errorText: { fontSize: tokens.fontSizeBase200, color: tokens.colorStatusDangerForeground1, marginTop: tokens.spacingVerticalXS, marginBottom: 0 },
  successText: { fontSize: tokens.fontSizeBase200, color: tokens.colorBrandForeground2, marginTop: tokens.spacingVerticalXS, marginBottom: 0 },

  priceBreakdown: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, fontSize: tokens.fontSizeBase300 },
  priceRow: { display: 'flex', justifyContent: 'space-between', color: tokens.colorNeutralForeground2 },
  priceRowBrand: { display: 'flex', justifyContent: 'space-between', color: tokens.colorBrandForeground2 },

  advanceNote: {
    fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground2,
    marginTop: tokens.spacingVerticalSNudge, marginLeft: '24px', marginBottom: 0,
  },

  totalRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginTop: tokens.spacingVerticalM, paddingTop: tokens.spacingVerticalM, paddingBottom: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  totalLabel: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  totalPrice: { fontSize: tokens.fontSizeBase600, fontWeight: tokens.fontWeightBold, color: tokens.colorNeutralForeground1, fontVariantNumeric: 'tabular-nums' },

  payseraImg: { height: '28px', marginBottom: tokens.spacingVerticalM, opacity: 0.6 },

  testModeBox: {
    padding: tokens.spacingVerticalM, borderRadius: tokens.borderRadiusLarge,
    border: `1px dashed ${tokens.colorStatusDangerBorder1}`,
    marginBottom: tokens.spacingVerticalL,
  },
  testModeLabel: { fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorStatusDangerForeground1, cursor: 'pointer' },

  orderErrorBox: {
    display: 'flex', alignItems: 'flex-start', gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalM, backgroundColor: tokens.colorStatusDangerBackground1,
    border: `1px solid ${tokens.colorStatusDangerBorder1}`, borderRadius: tokens.borderRadiusLarge,
    fontSize: tokens.fontSizeBase300, color: tokens.colorStatusDangerForeground1,
    marginBottom: tokens.spacingVerticalL,
  },
  orderErrorIcon: { fontSize: '16px', flexShrink: 0, marginTop: '2px' },

  submitBtn: {
    width: '100%', justifyContent: 'center',
    paddingTop: tokens.spacingVerticalL, paddingBottom: tokens.spacingVerticalL,
    fontSize: tokens.fontSizeBase400,
  },
});

function Checkout() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const s = useStyles();
  const [cartItems, setCartItems] = useState([]);
  const [baseTotalPrice, setBaseTotalPrice] = useState(0);
  const [finalPrice, setFinalPrice] = useState(0);
  const [isTestMode, setIsTestMode] = useState(IS_DEV);
  const [showCompanyFields, setShowCompanyFields] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const [hasDiscountCode, setHasDiscountCode] = useState(false);
  const [discountCodeInput, setDiscountCodeInput] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountError, setDiscountError] = useState('');

  const [hasGiftCard, setHasGiftCard] = useState(false);
  const [giftCardInput, setGiftCardInput] = useState('');
  const [appliedGiftCard, setAppliedGiftCard] = useState(null);
  const [giftCardError, setGiftCardError] = useState('');

  const [isAdvancePayment, setIsAdvancePayment] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState('atsiimsiu-pats');
  const [detailItem, setDetailItem] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [legalDialogSlug, setLegalDialogSlug] = useState(null);

  useEffect(() => { document.title = `${t('checkout_page.header')} | Fitit`; }, [t]);

  const [formData, setFormData] = useState({
    name: '', surname: '', email: '', phone: '',
    address: '', city: '', postalCode: '', comments: '',
    companyName: '', companyCode: '', companyAddress: '', vatCode: '',
  });

  useEffect(() => {
    const cart = JSON.parse(sessionStorage.getItem('furnitureCart')) || [];
    setCartItems(cart);
    setBaseTotalPrice(cart.reduce((acc, item) => acc + (item.price || 0), 0));
  }, []);

  useEffect(() => {
    let price = baseTotalPrice;
    if (appliedDiscount) price -= appliedDiscount.amount;
    if (appliedGiftCard) price -= appliedGiftCard.amount;
    if (price < 0) price = 0;
    if (isAdvancePayment) price = price / 2;
    setFinalPrice(price);
  }, [baseTotalPrice, appliedDiscount, appliedGiftCard, isAdvancePayment]);

  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '', surname: user.surname || '',
        email: user.email || '', phone: user.phone || '',
        address: user.address || '', city: user.city || '', postalCode: user.postalCode || ''
      }));
    }
  }, [isAuthenticated, user]);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFieldErrors(prev => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const applyDiscountCode = async () => {
    setDiscountError('');
    if (!discountCodeInput.trim()) return;
    try {
      const d = await apiGet(`/discount-codes/validate/${encodeURIComponent(discountCodeInput.trim())}`);
      setAppliedDiscount({ code: d.code, amount: d.amount });
    } catch (err) {
      setAppliedDiscount(null);
      setDiscountError(err.message || t('checkout_page.invalid_discount_code'));
    }
  };

  const applyGiftCard = async () => {
    setGiftCardError('');
    if (!giftCardInput.trim()) return;
    try {
      const d = await apiGet(`/gift-cards/validate/${encodeURIComponent(giftCardInput.trim().toUpperCase())}`);
      setAppliedGiftCard({ code: d.code, amount: d.amount });
    } catch (err) {
      setAppliedGiftCard(null);
      setGiftCardError(err.message || t('checkout_page.invalid_gift_card'));
    }
  };

  const handleOrder = async () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = t('checkout_page.err_name');
    if (!formData.email.trim()) errors.email = t('checkout_page.err_email');
    if (!formData.phone.trim()) errors.phone = t('checkout_page.err_phone');
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setOrderError(t('checkout_page.err_required'));
      return;
    }
    if (!termsAccepted) {
      setOrderError('Turite sutikti su Sąlygomis ir taisyklėmis, kad tęstumėte.');
      return;
    }
    setFieldErrors({});
    setLoading(true); setOrderError('');
    try {
      let currentUserId = user ? (user.id || user._id) : null;
      if (isAuthenticated && currentUserId) {
        await fetch(`${API}/user/profile`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phone: formData.phone, address: formData.address, city: formData.city, postalCode: formData.postalCode, surname: formData.surname }) });
      } else {
        const tempPassword = crypto.randomUUID ? crypto.randomUUID() : (Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)).toUpperCase();
        const regRes = await fetch(`${API}/auth/register`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: formData.name, email: formData.email, password: tempPassword }) });
        if (!regRes.ok) throw new Error(t('checkout_page.err_create_user'));
        const regData = await regRes.json();
        currentUserId = regData.user.id;
      }

      const itemsIds = (await Promise.all(cartItems.map(async (item) => {
        if (item._id) { fetch(`${API}/furnitures/${item.code}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ furnitureData: { userId: currentUserId, isOrdered: true } }) }).catch(() => {}); return item._id; }
        const getRes = await fetch(`${API}/furnitures/${item.code}`);
        if (getRes.ok) { const f = await getRes.json(); return f._id; }
        const postRes = await fetch(`${API}/furnitures`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ furnitureData: { ...item, userId: currentUserId, isOrdered: true } }) });
        if (postRes.ok) { const f = await postRes.json(); return f._id; }
        return null;
      }))).filter(Boolean);

      if (itemsIds.length === 0) throw new Error(t('checkout_page.err_save_items'));

      let fullValue = baseTotalPrice;
      if (appliedDiscount) fullValue -= appliedDiscount.amount;
      if (appliedGiftCard) fullValue -= appliedGiftCard.amount;
      if (fullValue < 0) fullValue = 0;

      const orderRes = await fetch(`${API}/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: itemsIds, userId: currentUserId, total: finalPrice, fullOrderValue: fullValue, isAdvancePayment,
          giftCardCode: appliedGiftCard?.code || null,
          discountDetails: (appliedDiscount || appliedGiftCard)
            ? { code: appliedDiscount?.code || appliedGiftCard?.code, amount: (appliedDiscount?.amount || 0) + (appliedGiftCard?.amount || 0) }
            : { code: null, amount: 0 },
          customerDetails: {
            name: formData.name, surname: formData.surname, email: formData.email, phone: formData.phone,
            address: deliveryMethod === 'pristatymas-i-namus' ? `${formData.address}, ${formData.city} ${formData.postalCode}`.trim() : 'Atsiima pats',
            deliveryMethod,
            company: showCompanyFields,
            companyName: showCompanyFields ? formData.companyName : '',
            companyCode: showCompanyFields ? formData.companyCode : '',
            companyAddress: showCompanyFields ? formData.companyAddress : '',
            vatCode: showCompanyFields ? formData.vatCode : '',
            comments: formData.comments,
          },
        }),
      });

      if (!orderRes.ok) { const err = await orderRes.json(); throw new Error(err.error || 'Serverio klaida'); }
      const newOrder = await orderRes.json();

      // Gift card (if any) is claimed atomically by the backend as part of order
      // creation now — no separate fire-and-forget follow-up call needed.

      sessionStorage.removeItem('furnitureCart');
      window.dispatchEvent(new Event('cartUpdated'));
      if (isTestMode) navigate(`/success/${newOrder.orderNumber}`);
      else submitPayseraForm(formData, finalPrice);
    } catch (err) { setOrderError(err.message); }
    finally { setLoading(false); }
  };

  const submitPayseraForm = (data, amount) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = PAYSERA_URL;
    [['name', data.name], ['email', data.email], ['amount', Math.round(amount * 100)]].forEach(([k, v]) => {
      const inp = document.createElement('input'); inp.type = 'hidden'; inp.name = k; inp.value = v; form.appendChild(inp);
    });
    document.body.appendChild(form); form.submit();
  };

  return (
    <div className={s.root}>

      <div className={s.headerBar}>
        <div className={s.headerInner}>
          <Breadcrumb items={[{ label: t('furniture_list.breadcrumb_home'), to: '/' }, { label: t('cart_page.header_title'), to: '/cart' }, { label: t('checkout_page.header') }]} />
          <h1 className={s.pageTitle}>{t('checkout_page.header')}</h1>
        </div>
      </div>

      {/* Item detail dialog — standartinis Fluent Dialog (ne rankomis rašytas overlay) */}
      <Dialog open={!!detailItem} onOpenChange={(_e, data) => { if (!data.open) setDetailItem(null); }}>
        <DialogSurface className={s.modalSurface}>
          <div className={s.modalLayout}>
          {detailItem?.snapshot3D && (
            <img src={detailItem.snapshot3D} alt="3D" className={s.modalImage} />
          )}
          <DialogBody className={s.modalBodyCol}>
            <DialogTitle
              action={<Button appearance="subtle" icon={<CloseIcon />} onClick={() => setDetailItem(null)} aria-label={t('topbar.close')} />}
            >
              {detailItem && getFurnitureLabel(detailItem, t)}
            </DialogTitle>
            <DialogContent>
              {detailItem?.code && (
                <p className={s.modalCode}>{detailItem.code}</p>
              )}
              {detailItem && (
                <div className={s.modalRows}>
                  {/* Dimensions */}
                  {detailItem.settings?.generalWidth > 0 && (
                    <div className={s.modalRow}>
                      <span>{t('furniture_item.dimensions')} W</span>
                      <span className={s.modalRowValue}>{Math.round(detailItem.settings.generalWidth * 100)} cm</span>
                    </div>
                  )}
                  {detailItem.settings?.generalHeight > 0 && (
                    <div className={s.modalRow}>
                      <span>{t('furniture_item.dimensions')} H</span>
                      <span className={s.modalRowValue}>{Math.round(detailItem.settings.generalHeight * 100)} cm</span>
                    </div>
                  )}
                  {detailItem.settings?.wallDepth > 0 && (
                    <div className={s.modalRow}>
                      <span>{t('furniture_item.dimensions')} D</span>
                      <span className={s.modalRowValue}>{Math.round(detailItem.settings.wallDepth)} cm</span>
                    </div>
                  )}
                  {detailItem.cabinets?.length > 0 && (
                    <div className={s.modalRow}>
                      <span>{t('checkout_page.detail_sections')}</span>
                      <span className={s.modalRowValue}>{detailItem.cabinets.length}</span>
                    </div>
                  )}
                  {(() => {
                    const n = (detailItem.cabinets || []).reduce((sum, c) => sum + (c.shelves?.length || 0), 0);
                    return n > 0 ? (
                      <div className={s.modalRow}>
                        <span>{t('checkout_page.detail_shelves')}</span>
                        <span className={s.modalRowValue}>{n}</span>
                      </div>
                    ) : null;
                  })()}
                  {getPlateLabel(detailItem.settings?.selectedPlate) && (
                    <div className={s.modalRow}>
                      <span>{t('checkout_page.detail_plate')}</span>
                      <span className={s.modalRowValue}>{getPlateLabel(detailItem.settings.selectedPlate)}</span>
                    </div>
                  )}
                  {getPlateLabel(detailItem.settings?.currentDoorPlate) && getPlateLabel(detailItem.settings?.currentDoorPlate) !== getPlateLabel(detailItem.settings?.selectedPlate) && (
                    <div className={s.modalRow}>
                      <span>{t('checkout_page.detail_door_plate')}</span>
                      <span className={s.modalRowValue}>{getPlateLabel(detailItem.settings.currentDoorPlate)}</span>
                    </div>
                  )}
                  {getHandleLabel(detailItem.settings?.doorHandles, t) && (
                    <div className={s.modalRow}>
                      <span>{t('checkout_page.detail_handles')}</span>
                      <span className={s.modalRowValue}>{getHandleLabel(detailItem.settings.doorHandles, t)}</span>
                    </div>
                  )}
                  {detailItem.services?.assemblyPrice > 0 && (
                    <div className={s.modalRow}>
                      <span>{t('checkout_page.detail_assembly')}</span>
                      <span className={s.modalRowValue}>+{formatPrice(detailItem.services.assemblyPrice, { showCents: false })}</span>
                    </div>
                  )}
                  {detailItem.services?.carryPrice > 0 && (
                    <div className={s.modalRow}>
                      <span>{t('checkout_page.detail_carrying')}</span>
                      <span className={s.modalRowValue}>+{formatPrice(detailItem.services.carryPrice, { showCents: false })}</span>
                    </div>
                  )}
                  {detailItem.price > 0 && (
                    <div className={s.modalTotalRow}>
                      <span className={s.modalTotalLabel}>{t('checkout_page.detail_price')}</span>
                      <span className={s.modalTotalValue}>{formatPrice(detailItem.price)}</span>
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="outline" onClick={() => setDetailItem(null)}>{t('topbar.close')}</Button>
            </DialogActions>
          </DialogBody>
          </div>
        </DialogSurface>
      </Dialog>

      <div className={s.container}>
        <div className={s.mainGrid}>

          {/* LEFT — FORM */}
          <div className={s.leftCol}>

            {/* Kontaktai */}
            <div className={s.card}>
              <div className={s.cardHeader}>
                <div className={s.iconChip}>
                  <PersonIcon className={s.chipIcon} />
                </div>
                <h2 className={s.cardTitle}>{t('checkout_page.your_data')}</h2>
              </div>
              <div className={s.fieldsGrid}>
                <FormField label={t('checkout_page.first_name')} name="name" value={formData.name} onChange={handleInputChange} placeholder="Jonas" required disabled={isAuthenticated} validationState={fieldErrors.name ? 'error' : undefined} validationMessage={fieldErrors.name} />
                <FormField label={t('checkout_page.last_name')} name="surname" value={formData.surname} onChange={handleInputChange} placeholder="Jonaitis" disabled={isAuthenticated} />
                <FormField label={t('checkout_page.email')} name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="jonas@pvz.lt" required disabled={isAuthenticated} validationState={fieldErrors.email ? 'error' : undefined} validationMessage={fieldErrors.email} />
                <FormField label={t('checkout_page.phone')} name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+370 600 00000" required validationState={fieldErrors.phone ? 'error' : undefined} validationMessage={fieldErrors.phone} />
              </div>

              <Checkbox
                className={s.checkboxRow}
                checked={showCompanyFields}
                onChange={(_, data) => setShowCompanyFields(!!data.checked)}
                label={{ children: t('checkout_page.company_buy'), className: s.checkboxLabel }}
              />

              {showCompanyFields && (
                <div className={s.companyFields}>
                  <FormField label={t('checkout_page.company_name')} name="companyName" value={formData.companyName} onChange={handleInputChange} placeholder="UAB Pavyzdys" />
                  <FormField label={t('checkout_page.company_code')} name="companyCode" value={formData.companyCode} onChange={handleInputChange} placeholder="123456789" />
                  <FormField label={t('checkout_page.company_addr')} name="companyAddress" value={formData.companyAddress} onChange={handleInputChange} placeholder="Gatvė 1, Vilnius" />
                  <FormField label={t('checkout_page.vat_code')} name="vatCode" value={formData.vatCode} onChange={handleInputChange} placeholder="LT123456789" />
                </div>
              )}
            </div>

            {/* Pristatymas */}
            <div className={s.card}>
              <div className={s.cardHeader}>
                <div className={s.iconChip}>
                  <ShippingIcon className={s.chipIcon} />
                </div>
                <h2 className={s.cardTitle}>{t('checkout_page.delivery_section')}</h2>
              </div>
              <RadioGroup
                className={s.deliveryOptions}
                value={deliveryMethod}
                onChange={(_, data) => setDeliveryMethod(data.value)}
              >
                <Radio value="atsiimsiu-pats" label={t('checkout_page.pickup')} />
                <Radio value="pristatymas-i-namus" label={t('checkout_page.home_delivery')} />
              </RadioGroup>
              {deliveryMethod === 'pristatymas-i-namus' && (
                <div className={s.deliveryAddress}>
                  <FormField label={t('checkout_page.city')} name="city" value={formData.city} onChange={handleInputChange} placeholder="Vilnius" />
                  <FormField label={t('checkout_page.postal')} name="postalCode" value={formData.postalCode} onChange={handleInputChange} placeholder="LT-00000" />
                  <div className={s.spanTwo}>
                    <FormField label={t('checkout_page.address')} name="address" value={formData.address} onChange={handleInputChange} placeholder="Gatvė, namas" />
                  </div>
                </div>
              )}
              <div className={s.commentsWrap}>
                <FormField label={t('checkout_page.comments')} name="comments" as="textarea" value={formData.comments} onChange={handleInputChange} placeholder={t('checkout_page.comments_ph')} />
              </div>
            </div>
          </div>

          {/* RIGHT — SUMMARY */}
          <div className={s.rightCol}>
            <div className={s.card}>
              <div className={s.cardHeader}>
                <div className={s.iconChip}>
                  <BagIcon className={s.chipIcon} />
                </div>
                <h2 className={s.cardTitle}>{t('checkout_page.summary')}</h2>
              </div>

              {cartItems.length === 0 ? (
                <p className={s.emptyCartText}>{t('checkout_page.empty_cart')}</p>
              ) : (
                <div className={s.cartList}>
                  {cartItems.map((item, i) => (
                    <button
                      key={i}
                      onClick={() => setDetailItem(item)}
                      className={s.cartRow}
                    >
                      <div className={s.cartRowLeft}>
                        {item.snapshot3D && (
                          <img src={item.snapshot3D} alt="" className={s.cartRowImg} />
                        )}
                        <span className={s.cartRowLabel}>{getFurnitureLabel(item, t)}</span>
                      </div>
                      <span className={s.cartRowPrice}>{item.price > 0 ? formatPrice(item.price) : '–'}</span>
                    </button>
                  ))}
                </div>
              )}

              <div className={s.divider} />

              {!hasDiscountCode && !hasGiftCard && (
                <p className={s.discountHint}>
                  <OfferIcon fontSize={14} />
                  {t('checkout_page.discount_hint')}
                </p>
              )}

              {/* Discount code */}
              <div className={s.discountBlock}>
                <Checkbox
                  className={s.discountCheckboxRow}
                  checked={hasDiscountCode}
                  onChange={(_, data) => setHasDiscountCode(!!data.checked)}
                  label={{ children: t('checkout_page.has_discount'), className: s.checkboxLabel }}
                />
                {hasDiscountCode && (
                  <div className={s.discountReveal}>
                    <div className={s.discountInputRow}>
                      <Input className={s.discountInput} placeholder={t('checkout_page.discount_ph')} value={discountCodeInput} onChange={e => setDiscountCodeInput(e.target.value)} disabled={!!appliedDiscount} />
                      {!appliedDiscount
                        ? <Button appearance="outline" size="small" className={s.whitespaceNoWrap} onClick={applyDiscountCode}>{t('checkout_page.apply')}</Button>
                        : <Button appearance="outline" size="small" className={s.dangerOutlineBtn} onClick={() => { setAppliedDiscount(null); setDiscountCodeInput(''); }}>✕</Button>
                      }
                    </div>
                    {discountError && <p className={s.errorText}>{discountError}</p>}
                    {appliedDiscount && <p className={s.successText}>{t('checkout_page.applied_amount', { amount: formatPrice(appliedDiscount.amount) })}</p>}
                  </div>
                )}
              </div>

              {/* Gift card */}
              <div className={s.giftBlock}>
                <Checkbox
                  className={s.discountCheckboxRow}
                  checked={hasGiftCard}
                  onChange={(_, data) => setHasGiftCard(!!data.checked)}
                  label={{ children: t('checkout_page.has_gift'), className: s.checkboxLabel }}
                />
                {hasGiftCard && (
                  <div className={s.discountReveal}>
                    <div className={s.discountInputRow}>
                      <Input className={s.discountInput} placeholder={t('checkout_page.gift_ph')} value={giftCardInput} onChange={e => setGiftCardInput(e.target.value)} disabled={!!appliedGiftCard} />
                      {!appliedGiftCard
                        ? <Button appearance="outline" size="small" className={s.whitespaceNoWrap} onClick={applyGiftCard}>{t('checkout_page.apply')}</Button>
                        : <Button appearance="outline" size="small" className={s.dangerOutlineBtn} onClick={() => { setAppliedGiftCard(null); setGiftCardInput(''); }}>✕</Button>
                      }
                    </div>
                    {giftCardError && <p className={s.errorText}>{giftCardError}</p>}
                    {appliedGiftCard && <p className={s.successText}>{t('checkout_page.applied_amount', { amount: formatPrice(appliedGiftCard.amount) })}</p>}
                  </div>
                )}
              </div>

              {/* Price breakdown — "Suma" (bazinė kaina) rodoma TIK kai yra
                  nuolaida/dovanų čekis (t.y. skiriasi nuo galutinės "Mokėti"
                  sumos apačioje) — be nuolaidos ji tiesiog dubliuodavo tą pačią
                  sumą (žr. /admin/bugs "sia eilute istrink nes suma jau
                  matome apacioje"). */}
              {(appliedDiscount || appliedGiftCard) && (
              <div className={s.priceBreakdown}>
                <div className={s.priceRow}>
                  <span>{t('checkout_page.amount')}</span><span>{formatPrice(baseTotalPrice)}</span>
                </div>
                {appliedDiscount && (
                  <div className={s.priceRowBrand}>
                    <span>{t('checkout_page.discount_applied', { code: appliedDiscount.code })}</span>
                    <span>-{formatPrice(appliedDiscount.amount)}</span>
                  </div>
                )}
                {appliedGiftCard && (
                  <div className={s.priceRowBrand}>
                    <span>{t('checkout_page.gift_card_applied', { code: appliedGiftCard.code })}</span>
                    <span>-{formatPrice(appliedGiftCard.amount)}</span>
                  </div>
                )}
              </div>
              )}

              {/* Advance payment — paprastas list item, kaip nuolaidos/dovanų
                  čekio varnelės, be žalio blokelio (žr. /admin/bugs "Perkelk
                  apmoketi avansa kaip prie kitu checkbox list item"). */}
              <div className={s.giftBlock}>
                <Checkbox
                  className={s.discountCheckboxRow}
                  checked={isAdvancePayment}
                  onChange={(_, data) => setIsAdvancePayment(!!data.checked)}
                  label={{ children: t('checkout_page.advance_title'), className: s.checkboxLabel }}
                />
                {isAdvancePayment && (
                  <p className={s.advanceNote}>{t('checkout_page.advance_note')}</p>
                )}
              </div>

              {/* Total */}
              <div className={s.totalRow}>
                <span className={s.totalLabel}>{t('checkout_page.pay_label')} {isAdvancePayment ? t('checkout_page.advance_tag') : ''}</span>
                <span className={s.totalPrice}>{formatPrice(finalPrice, { showCents: false })}</span>
              </div>

              <div className={s.dividerMb} />
              <img src={payseraImage} alt="Paysera" className={s.payseraImg} />

              {IS_DEV && (
                <div className={s.testModeBox}>
                  <Checkbox
                    checked={isTestMode}
                    onChange={(_, data) => setIsTestMode(!!data.checked)}
                    label={{ children: 'TEST MODE (dev only)', className: s.testModeLabel }}
                  />
                </div>
              )}

              {orderError && (
                <div className={s.orderErrorBox}>
                  <ErrorIcon className={s.orderErrorIcon} />
                  {orderError}
                </div>
              )}

              <p className={s.privacyNotice}>
                Paspaudę „{isTestMode ? t('checkout_page.simulate_btn') : t('checkout_page.pay_btn')}“ patvirtinate, kad perskaitėte ir sutinkate su mūsų{' '}
                <button type="button" className={s.legalLink} onClick={() => setLegalDialogSlug('privatumo-politika')}>Privatumo politika</button>.
              </p>

              <Checkbox
                className={s.termsCheckboxRow}
                checked={termsAccepted}
                onChange={(_, data) => setTermsAccepted(!!data.checked)}
                label={{
                  children: (
                    <span className={s.checkboxLabel}>
                      Aš perskaičiau ir sutinku su svetainės{' '}
                      <button type="button" className={s.legalLink} onClick={() => setLegalDialogSlug('taisykles')}>Sąlygomis ir taisyklėmis</button>
                    </span>
                  ),
                }}
              />

              <Button
                appearance="primary"
                size="large"
                className={s.submitBtn}
                onClick={handleOrder}
                disabled={cartItems.length === 0 || loading || !termsAccepted}
              >
                {loading
                  ? <Spinner size="tiny" />
                  : isTestMode ? t('checkout_page.simulate_btn') : t('checkout_page.pay_btn')
                }
              </Button>
            </div>
          </div>
        </div>
      </div>

      <LegalTextDialog slug={legalDialogSlug} onClose={() => setLegalDialogSlug(null)} />
    </div>
  );
}

export default Checkout;
